import google, { mutators, OverlayRegistry, OverlayType, toLatLng, verify } from "@churchofjesuschrist/maps-google";
import MapsService, { IconType, LocationType } from "@churchofjesuschrist/maps-service";
import { iconPinHeightDefault, iconSizeDefault } from "@churchofjesuschrist/maps-constants";
import { defaultEmpty, defaultUndefined, isDefined, isString, removeListeners } from "@churchofjesuschrist/maps-utils";
let initialized = false;
class Marker {
  constructor(options = {}) {
    if (!initialized) {
      verify();
      construct();
      initialized = true;
    }
    this.setOptions(options);
  }
}

//TODO: move to static class property when jsstack eslint allows
Marker.MAX_ZINDEX = 1000000;
const construct = () => {
  const markerProps = ["animation", "clickable", "crossOnDrag", "cursor", "draggable", "map", "opacity", "optimized", "position", "title", "visible", "zIndex"];
  const forwardEvents = ["animation_changed", "click", "clickable_changed", "cursor_changed", "dblclick", "drag", "dragend", "draggable_changed", "dragstart", "flat_changed", "icon_changed", "mousedown", "mouseout", "mouseover", "mouseup", "position_changed", "rightclick", "contextmenu", "shape_changed", "title_changed", "visible_changed", "zindex_changed"];
  Object.setPrototypeOf(Marker.prototype, Object.assign(new google.maps.MVCObject(), mutators("icon", ...markerProps), (() => {
    function onAdd() {
      onRemove.call(this);
      if (!this.$marker) {
        let iconOptions = getMarkerIconOptions.call(this);
        let markerOptions = markerProps.reduce((map, prop) => {
          let value = this.get(prop);
          if (isDefined(value)) {
            map[prop] = value;
          }
          return map;
        }, iconOptions);
        this.$marker = new google.maps.Marker(markerOptions);
        markerProps.forEach(prop => {
          this.$marker.bindTo(prop, this);
        });
        this.$listeners = forwardEvents.map(eventName => {
          return this.$marker.addListener(eventName, (...args) => {
            google.maps.event.trigger(this, eventName, ...args);
          });
        });
      } else {
        onIconUpdate.call(this);
      }
    }
    function onRemove() {
      if (this.$marker) {
        this.$marker.unbindAll();
        this.$marker.setMap(null);
        delete this.$marker;
      }
      if (removeListeners(this.$listeners)) {
        delete this.$listeners;
      }
    }
    function onIconUpdate() {
      if (this.$marker) {
        let iconOptions = getMarkerIconOptions.call(this);
        this.$marker.setOptions(iconOptions);
      }
    }
    function getMarkerIconOptions() {
      return {
        icon: {
          url: getIconUrl.call(this),
          anchor: getIconAnchor.call(this)
        },
        shape: getIconShape.call(this)
      };
    }
    function getIconUrl() {
      let icon = this.getIcon() || {},
        options = {
          type: getIconType.call(this),
          ...icon
        };
      return MapsService.getIconUrl(options);
    }
    function getIconScale() {
      let icon = this.getIcon() || {};
      return defaultEmpty(icon.scale, 1);
    }
    function getIconType() {
      let icon = this.getIcon() || {};
      return defaultUndefined(icon.type, IconType.DOT);
    }
    function getIconSize() {
      let scale = getIconScale.call(this),
        type = getIconType.call(this),
        width = Math.ceil(iconSizeDefault * scale),
        height = type !== IconType.PIN ? width : Math.ceil(iconPinHeightDefault * scale);
      return new google.maps.Size(width, height);
    }
    function getIconAnchor() {
      let icon = this.getIcon() || {},
        size = getIconSize.call(this),
        center = size.width / 2,
        type = getIconType.call(this),
        anchor = type !== IconType.PIN ? new google.maps.Point(center, center) : new google.maps.Point(center, size.height);
      if (icon.anchor) {
        anchor.x += icon.anchor.x;
        anchor.y += icon.anchor.y;
      }
      return anchor;
    }
    function getIconShape() {
      let {
          width,
          height
        } = getIconSize.call(this),
        type = getIconType.call(this);
      switch (type) {
        case IconType.PIN:
          {
            //TODO: verify shape
            return {
              type: "poly",
              coords: [width * 0.5, 0.038, width * 0.625, height * 0.051, width * 0.75, height * 0.094, width * 0.875, height * 0.186, width * 0.946, height * 0.36, width * 0.875, height * 0.535, width * 0.75, height * 0.692, width * 0.625, height * 0.854, width * 0.5, height * 0.963, width * 0.375, height * 0.854, width * 0.25, height * 0.692, width * 0.125, height * 0.535, width * 0.054, height * 0.36, width * 0.125, height * 0.186, width * 0.25, height * 0.094, width * 0.375, height * 0.051, width * 0.5, 0.038]
            };
          }
        case IconType.DOT:
          {
            //TODO: verify shape
            let center = width / 2,
              radius = width * 0.443;
            return {
              type: "circle",
              coords: [center, center, radius]
            };
          }
      }
      return null;
    }
    return {
      setIcon: function (icon) {
        if (icon && isString(icon)) {
          icon = {
            layer: icon
          };
        }
        this.set("icon", icon);
      },
      setPosition: function (position) {
        this.set("position", toLatLng(position));
      },
      getIconSize: function () {
        return getIconSize.call(this);
      },
      getIconAnchor: function () {
        return getIconAnchor.call(this);
      },
      changed: function (key) {
        switch (key) {
          case "map":
            if (this.getMap()) {
              onAdd.call(this);
            } else {
              onRemove.call(this);
            }
            break;
          case "icon":
            onIconUpdate.call(this);
            break;
        }
      }
    };
  })()));
  Object.setPrototypeOf(Marker, google.maps.Marker);
};
OverlayRegistry.register("Marker", Marker, OverlayType.MARKER);
export default Marker;
export { IconType, LocationType };